import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { TitleCasePipe, NgClass } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ToastService } from '../../ui';
import { MatDialog } from '@angular/material/dialog';
import { catchError, forkJoin, of } from 'rxjs';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { SessionService } from '../../core/session/session.service';
import { AuthService } from '../../core/auth/auth.service';
import { LoggingService } from '../../core/services/logging.service';
import { safeHttpMessage } from '../../core/error/safe-http-message';
import { PhoneServiceApiService } from '../../core/services/phone-service-api.service';
import { Language } from '../../core/services/phone-service-api.model';
import { NotificationsForwardingsService } from '../notifications-forwardings/notifications-forwardings.service';
import { CcNumbersService } from './cc-numbers.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { extractErrorMessage } from '../../shared/utils/extract-error-message';
import { copyToClipboard } from '../../shared/utils/clipboard';
import { SNACKBAR_ERROR_CONFIG } from '../../shared/utils/notification-defaults';
import { AmBookDialogComponent, AmBookDialogData } from './am-book-dialog.component';
import { environment } from '../../../environments/environment';
import { BookNumberDialogComponent, BookNumberDialogData, BookNumberDialogResult } from './book-number-dialog.component';
import { NumberDetailsDialogComponent, NumberDetailsDialogData } from './number-details-dialog.component';
import { FixRegulationDialogComponent, FixRegulationDialogData } from './fix-regulation-dialog.component';
import {
  RemoveTagConfirmDialogComponent,
  RemoveTagConfirmData,
  RemoveTagConfirmResult,
} from './remove-tag-confirm-dialog.component';
import {
  PhoneNumber,
  PhoneNumberType,
  NumberTag,
  WaitingQueueStatus,
  AnsweringMachineInfo,
  AmBookPayload,
  VOXBONE_ERROR_STATUSES,
  VOXBONE_PENDING_STATUSES,
} from './cc-numbers.model';
import {
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HlmSpinnerComponent,
  HlmTooltipDirective,
  HlmSelectOption,
} from '../../ui';

/** sessionStorage key remembering the "don't ask again" choice for tag removal. */
const TAG_REMOVE_OPT_OUT_KEY = 'cmt-cc-numbers-tag-remove-ack';

@Component({
  selector: 'cmt-cc-numbers',
  imports: [
    FormsModule,
    TitleCasePipe,
    NgClass,
    ErrorStateComponent,
    HlmBadgeComponent, HlmBtnDirective, HlmIconComponent, HlmInputDirective,
    HlmSelectComponent, HlmSpinnerComponent, HlmTooltipDirective,
  ],
  templateUrl: './cc-numbers.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CcNumbersComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(CcNumbersService);
  private readonly nfApi = inject(NotificationsForwardingsService);
  private readonly psApi = inject(PhoneServiceApiService);
  readonly session = inject(SessionService);
  private readonly auth = inject(AuthService);
  private readonly loggingService = inject(LoggingService);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  // --- State ---
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly numbers = signal<PhoneNumber[]>([]);
  readonly wqStatus = signal<WaitingQueueStatus | null>(null);
  readonly languages = signal<Language[]>([]);
  readonly amInfo = signal<AnsweringMachineInfo | null>(null);

  // --- Derived ---
  readonly callcenterNumbers = computed(() =>
    this.numbers().filter(n => n.type !== 'VOICEBOX'),
  );
  readonly voiceboxNumber = computed(() =>
    this.numbers().find(n => n.type === 'VOICEBOX') ?? null,
  );
  readonly hasVoicebox = computed(() =>
    this.amInfo()?.voiceBoxNumber != null || this.numbers().some(n => n.type === 'VOICEBOX'),
  );
  readonly amActive = computed(() => this.hasVoicebox());
  /** True on local/test (tsrv) — the AM runs on a separate system there while the
   *  test DB is a days-old live copy, so AM data (e.g. language) can be stale.
   *  Drives the warning note under the AM language select (EB-3872). */
  readonly isTestEnvironment = environment.authUrl.includes('tsrv');
  /** Voicebox number to display — AM info preferred, numbers list as fallback
   *  (the AM-info endpoint can return an empty body despite an existing voicebox). */
  readonly amVoiceboxDisplayNumber = computed(() =>
    this.amInfo()?.voiceBoxNumber?.number ?? this.voiceboxNumber()?.number ?? null,
  );

  readonly nonVoiceboxCount = computed(() => this.callcenterNumbers().length);

  // --- AM booking (decoupled from the number rows) ---
  // Old CMT offers AM booking on CALLCENTER and HIDDEN numbers only (not SERVICE).
  readonly ccNumberSelectOptions = computed<HlmSelectOption<string>[]>(() =>
    this.callcenterNumbers()
      .filter(n => n.type === 'CALLCENTER' || n.type === 'HIDDEN')
      .map(n => ({ value: n.number, label: `${n.number} (${n.type})` })),
  );

  /**
   * Book a new answering machine — the target CC number is chosen inside the
   * dialog (old CMT: pick a public number, then book). A single primary button
   * triggers this, mirroring "Add Number" / "Activate".
   */
  openAmBookDialog(): void {
    const dialogRef = this.dialog.open(AmBookDialogComponent, {
      data: {
        languages: this.languages(),
        numberOptions: this.ccNumberSelectOptions(),
      } as AmBookDialogData,
    });

    dialogRef.afterClosed().subscribe((result: AmBookPayload | null) => {
      if (!result?.number) return;
      const { number, ...body } = result;
      this.saving.set(true);
      this.api.bookAnsweringMachine(this.phoneServiceId, number, String(this.auth.currentUserId), body)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.loadAll();
            this.toast.open('Answering machine booked', 'OK', { duration: 3000 });
          },
          error: (err) => {
            this.saving.set(false);
            this.toast.open(extractErrorMessage(err, 'Failed to book answering machine'), 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
    });
  }

  /**
   * Activate the existing AM for one more CC number (EB-3872). Old CMT 'active'
   * mode: only the voicebox type is asked, then POSTed for that number.
   */
  activateAmForNumber(n: PhoneNumber): void {
    // Defence in depth for the *parity* rule only: a rejected or pending Voxbone regulation
    // must not be bookable from anywhere, template or not (PR #42 review H3). Deliberately
    // not the full canActivateAm() - amActive() and isAmLinked() are availability conditions
    // for the button, and re-checking them here would make the handler untestable in
    // isolation without adding any data safety.
    if (!this.amActionable(n)) return;
    const dialogRef = this.dialog.open(AmBookDialogComponent, {
      data: {
        activateOnly: true,
        number: n.number,
        currentType: this.amInfo()?.type ?? null,
        languages: this.languages(),
      } as AmBookDialogData,
    });

    dialogRef.afterClosed().subscribe((result: AmBookPayload | null) => {
      if (!result?.type) return;
      this.saving.set(true);
      this.api.bookAnsweringMachine(this.phoneServiceId, n.number, String(this.auth.currentUserId), { type: result.type })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.loadAll();
            this.toast.open(`Answering machine activated for ${n.number}`, 'OK', { duration: 3000 });
          },
          error: (err) => {
            this.saving.set(false);
            this.toast.open(extractErrorMessage(err, 'Failed to activate answering machine'), 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
    });
  }

  readonly canActivateWq = computed(() =>
    this.nonVoiceboxCount() > 0 && this.wqStatus()?.status !== 'ACTIVE',
  );

  /** Set of number strings that have AM active (from amInfo or direct forwardings) */
  readonly amLinkedNumbers = signal<Set<string>>(new Set());

  // --- Row edit (Type + Tags) ---
  /** Id of the row currently in edit mode (Type select + tag chips become editable). */
  readonly editingRowId = signal<number | null>(null);
  /** Draft text for the "add tag" input of the row in edit mode. */
  readonly newTagName = signal('');

  readonly saving = signal(false);

  readonly languageSelectOptions = computed<HlmSelectOption<string>[]>(() =>
    this.languages().map(l => ({ value: l.abbreviation, label: l.name })),
  );

  private phoneServiceId = 0;

  /** Check if a number has AM linked */
  isAmLinked(n: PhoneNumber): boolean {
    return this.amLinkedNumbers().has(n.number);
  }

  /**
   * The part of old CMT's gate that both per-number AM actions share.
   *
   * Old CMT applies `!number.status.show` to Book **and** Deactivate alike — the two
   * buttons differ only in `answerMachineState`
   * ([`numbers.html#lines-52:60`](https://bitbucket.org/egroupio/customer-management-tool/src/e8410a8/app/src/states/numbers/numbers.html#lines-52:60)):
   *
   * ```
   * ng-click="bookAnswerMachineDialog()"
   * ng-show="!number.status.show && !deleteInProgress[number.id]
   *          && number.answerMachineState=='inactive'
   *          && (number.type=='CALLCENTER' || number.type=='HIDDEN')"
   *
   * ng-click="deactivateAnswerMachine()"
   * ng-show="!number.status.show && !deleteInProgress[number.id]
   *          && number.answerMachineState=='active'
   *          && (number.type=='CALLCENTER' || number.type=='HIDDEN')"
   * ```
   *
   * AMIE gated only Deactivate, so on a number whose Voxbone regulation was rejected or
   * is still pending the operator could book the answering machine but never un-book it —
   * a one-way door (PR #42 review H3). `status.show` is true exactly for those rows, which
   * is what {@link isVoxboneError} / {@link isVoxbonePending} express here.
   *
   * The exclusion is per *action*, not per row: old CMT still renders the number with its
   * status block (`numbers.html:38`), which is why the list keeps them too.
   */
  private amActionable(n: PhoneNumber): boolean {
    return (n.type === 'CALLCENTER' || n.type === 'HIDDEN')
      && !this.isVoxboneError(n)
      && !this.isVoxbonePending(n);
  }

  canDeactivateAm(n: PhoneNumber): boolean {
    return this.isAmLinked(n) && this.amActionable(n);
  }

  canActivateAm(n: PhoneNumber): boolean {
    return !this.isAmLinked(n) && this.amActive() && this.amActionable(n);
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = Number(params['phoneServiceId']);
      if (id) {
        this.phoneServiceId = id;
        this.loadAll();
      }
    });
  }

  loadAll(): void {
    this.loading.set(true);
    this.error.set(null);

    forkJoin({
      numbers: this.api.getNumbers(this.phoneServiceId),
      wq: this.api.getWaitingQueueStatus(this.phoneServiceId),
      // AM languages are the full (non-brand-filtered) list — old CMT parity.
      // No brand dependency, so no hydration race either.
      languages: this.psApi.getAllLanguages(),
      amInfo: this.api.getAmInfo(this.phoneServiceId).pipe(
        catchError((err) => {
          // EB-3798: non-fatal AM-info failure — metadata only.
          this.loggingService.warn('cc_numbers.am_load_failed', {
            httpStatus: err.status,
            full_message: safeHttpMessage(err),
          });
          return of(null);
        }),
      ),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ numbers, wq, languages, amInfo }) => {
        this.numbers.set(numbers);
        this.wqStatus.set(wq);
        this.languages.set(languages as Language[]);
        this.amInfo.set(amInfo);

        // Build AM linked numbers set
        if (amInfo?.linkedCallCenterNumbers?.length) {
          // Use AM info API data — most accurate
          const linked = new Set(amInfo.linkedCallCenterNumbers
            .filter(n => n.type !== 'VOICEBOX')
            .map(n => n.number));
          this.amLinkedNumbers.set(linked);
          this.loading.set(false);
        } else if (numbers.some(n => n.type === 'VOICEBOX')) {
          // Fallback: voicebox exists but AM info empty — check direct forwardings
          this.detectAmViaForwardings(numbers.filter(n => n.type !== 'VOICEBOX'));
        } else {
          this.amLinkedNumbers.set(new Set());
          this.loading.set(false);
        }
      },
      error: (err) => {
        this.error.set(extractErrorMessage(err, 'Failed to load numbers'));
        this.loading.set(false);
      },
    });
  }

  /** Fallback AM detection: check direct forwardings for voicebox destinations (900/902/905) */
  private detectAmViaForwardings(ccNumbers: PhoneNumber[]): void {
    if (ccNumbers.length === 0) {
      this.amLinkedNumbers.set(new Set());
      this.loading.set(false);
      return;
    }

    const checks = ccNumbers.map(n =>
      this.nfApi.getDirectForwardings(this.phoneServiceId, n.number).pipe(
        catchError(() => of(null)),
      ),
    );

    forkJoin(checks).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(results => {
      const linked = new Set<string>();
      results.forEach((res, i) => {
        if (!res) return;
        const hasAmForwarding = res.some(f => {
          const dest = f.reachability?.destination ?? '';
          return dest.startsWith('900') || dest.startsWith('902') || dest.startsWith('905');
        });
        if (hasAmForwarding) {
          linked.add(ccNumbers[i].number);
        }
      });
      this.amLinkedNumbers.set(linked);
      this.loading.set(false);
    });
  }

  // --- Clipboard ---

  copyToClipboard(text: string, event?: Event): void {
    copyToClipboard(this.toast, text, event);
  }

  /** Readable split notation for the table: "vorwahl präfix suffix" (e.g. "+4930 52004 7580"). Falls back to the raw number. */
  splitNumber(n: PhoneNumber): string {
    return n.numberBlock ? `${n.numberBlock.vorwahl} ${n.numberBlock.prefix} ${n.suffix ?? ''}`.trimEnd() : n.number;
  }

  // --- Voxbone status helpers ---

  isVoxboneError(n: PhoneNumber): boolean {
    const status = n.currentRegulationStatus;
    return status != null && VOXBONE_ERROR_STATUSES.includes(status);
  }

  isVoxbonePending(n: PhoneNumber): boolean {
    const status = n.currentRegulationStatus;
    return status != null && VOXBONE_PENDING_STATUSES.includes(status);
  }

  voxboneTooltip(n: PhoneNumber): string {
    const status = n.currentRegulationStatus;
    if (!status) return '';
    return `Regulation: ${status.replace(/_/g, ' ')}`;
  }

  // --- Row Edit (Type + Tags) ---
  //
  // One inline edit mode per row, fully "live": the Type select persists on change and tags
  // add/remove on their own per-tag endpoints — there is no staged state, so the only control
  // needed to leave the mode is "Done". Tag removal is confirmed (customer-visible data) unless
  // the operator opted out for the session.

  startRowEdit(n: PhoneNumber): void {
    this.editingRowId.set(n.id);
    this.newTagName.set('');
  }

  closeRowEdit(): void {
    this.editingRowId.set(null);
    this.newTagName.set('');
  }

  /** Persist a number-type change immediately and patch it into the list (keeps edit mode open). */
  changeType(n: PhoneNumber, newType: PhoneNumberType): void {
    if (newType === n.type || this.saving()) return;
    this.saving.set(true);
    this.api.editNumberType(this.phoneServiceId, n.id, newType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.numbers.update(list => list.map(x => x.id === n.id ? { ...x, type: newType } : x));
          this.toast.open('Number type updated', 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.saving.set(false);
          this.toast.open(extractErrorMessage(err, 'Failed to update number type'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
  }

  // --- Tags (inline, immediate; mutate customer-visible data) ---

  /** Add the drafted tag to the number. Persists immediately; the PS-API normalizes to UPPERCASE. */
  addNumberTag(n: PhoneNumber): void {
    const name = this.newTagName().trim();
    if (!name || this.saving()) return;
    this.saving.set(true);
    this.api.addTag(this.phoneServiceId, n.number, name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tag) => {
          this.saving.set(false);
          this.newTagName.set('');
          // Dedupe: re-adding an existing (normalized) tag returns the same id.
          this.patchNumberTags(n.id, tags => tags.some(t => t.id === tag.id) ? tags : [...tags, tag]);
          this.toast.open(`Tag "${tag.name}" added`, 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.saving.set(false);
          this.toast.open(extractErrorMessage(err, 'Failed to add tag'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
  }

  /**
   * Remove a tag. Tags are visible to the customer, so removal is confirmed — unless the operator
   * opted out for the session via the confirm dialog's checkbox.
   */
  confirmRemoveTag(n: PhoneNumber, tag: NumberTag): void {
    if (this.saving()) return;
    if (sessionStorage.getItem(TAG_REMOVE_OPT_OUT_KEY) === 'true') {
      this.removeTag(n, tag);
      return;
    }
    const dialogRef = this.dialog.open(RemoveTagConfirmDialogComponent, {
      data: { tagName: tag.name, number: n.number } as RemoveTagConfirmData,
    });
    dialogRef.afterClosed().subscribe((result: RemoveTagConfirmResult | undefined) => {
      if (!result?.proceed) return;
      if (result.remember) sessionStorage.setItem(TAG_REMOVE_OPT_OUT_KEY, 'true');
      this.removeTag(n, tag);
    });
  }

  private removeTag(n: PhoneNumber, tag: NumberTag): void {
    this.saving.set(true);
    this.api.deleteTag(this.phoneServiceId, n.number, tag.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.patchNumberTags(n.id, tags => tags.filter(t => t.id !== tag.id));
          this.toast.open(`Tag "${tag.name}" removed`, 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.saving.set(false);
          this.toast.open(extractErrorMessage(err, 'Failed to remove tag'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
  }

  /** Immutably update the tags of a single number in the list signal (no full reload needed). */
  private patchNumberTags(numberId: number, update: (tags: NumberTag[]) => NumberTag[]): void {
    this.numbers.update(list =>
      list.map(n => n.id === numberId ? { ...n, tags: update(n.tags ?? []) } : n),
    );
  }

  // --- Delete Number ---

  confirmDeleteNumber(n: PhoneNumber): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Number',
        message: `Delete number ${n.number}? This may affect the waiting queue configuration.`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.api.deleteNumber(this.phoneServiceId, n.id, String(this.auth.currentUserId))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.loadAll();
            this.toast.open(`Number ${n.number} deleted`, 'OK', { duration: 3000 });
          },
          error: (err) => {
            this.toast.open(extractErrorMessage(err, 'Failed to delete number'), 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
    });
  }

  // --- Number Details (read-only techie info) ---

  openDetails(n: PhoneNumber): void {
    const dialogRef = this.dialog.open(NumberDetailsDialogComponent, {
      data: { number: n, phoneServiceId: this.phoneServiceId } as NumberDetailsDialogData,
      // Wide enough for the Number / Number Block two-column reflow (matches the lg dialog surface).
      width: '760px',
      maxWidth: '95vw',
    });

    // The details dialog is read-only except for "Fix regulation" — reload the list if a fix was submitted.
    dialogRef.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.loadAll();
    });
  }

  /**
   * Open the Fix-regulation dialog directly for a failed/rejected number — the table status ✕ routes
   * here (not the read-only details dialog) so the repair action is one click away and unambiguous.
   * The dialog is self-contained: it shows the rejection reasons + the prefilled editable form.
   * Reloads the list on a successful re-submission.
   */
  openFixRegulation(n: PhoneNumber): void {
    const dialogRef = this.dialog.open(FixRegulationDialogComponent, {
      data: {
        phoneServiceId: this.phoneServiceId,
        number: n.number,
        numberType: n.type,
        rejectionReasons: n.rejectionReasons,
        cityPrefix: n.numberBlock?.cityPrefix ?? null,
      } as FixRegulationDialogData,
      // Wide (lg) so the requirements-driven auto-fill form grid uses ~4 columns → far less vertical scroll.
      width: '880px',
      maxWidth: '95vw',
      // Anchor near the top instead of vertically centering — tighter top gap for this tall form dialog.
      position: { top: '2rem' },
    });
    dialogRef.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.loadAll();
    });
  }

  // --- Book Number (dialog) ---

  openBookNumberDialog(): void {
    const brandId = this.session.brandId();
    if (!brandId) return;

    const dialogRef = this.dialog.open(BookNumberDialogComponent, {
      data: {
        brandId,
        phoneServiceId: this.phoneServiceId,
        employeeId: String(this.auth.currentUserId),
      } as BookNumberDialogData,
      width: '960px',
      maxWidth: '95vw',
    });

    dialogRef.afterClosed().subscribe((result: BookNumberDialogResult | undefined) => {
      if (!result) return;
      this.saving.set(true);
      this.api.bookNumber(this.phoneServiceId, result)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.loadAll();
            this.toast.open('Number booked successfully', 'OK', { duration: 3000 });
          },
          error: (err) => {
            this.saving.set(false);
            this.toast.open(extractErrorMessage(err, 'Failed to book number'), 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
    });
  }

  // --- Answering Machine ---

  /**
   * Switch the AM off for a single number — old CMT `number.js:149-153`
   * (PUT .../numbers/{number}/answeringmachine). The AM stays booked for the
   * phone service; removing it entirely is the PS-wide `cancelAm()`. The
   * confirm spells that scope out so the two actions can't be confused.
   */
  deactivateAm(n: PhoneNumber): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Deactivate Answering Machine',
        message: `This deactivates the answering machine for ${n.number} only. It stays booked for the phone service and keeps answering on the other numbers.`,
        confirmLabel: 'Deactivate',
        confirmColor: 'warn',
        icon: 'power_settings_new',
      },
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.saving.set(true);
      this.api.deactivateAnsweringMachine(this.phoneServiceId, n.number)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.loadAll();
            this.toast.open(`Answering machine deactivated for ${n.number}`, 'OK', { duration: 3000 });
          },
          error: (err) => {
            this.saving.set(false);
            this.toast.open(extractErrorMessage(err, 'Failed to deactivate answering machine'), 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
    });
  }

  updateAmLanguage(lang: string): void {
    this.saving.set(true);
    this.api.updateAnsweringMachineLanguage(this.phoneServiceId, lang)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.loadAll();
          this.toast.open('AM language updated', 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.saving.set(false);
          this.toast.open(extractErrorMessage(err, 'Failed to update AM language'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
  }

  cancelAm(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Cancel Answering Machine',
        message: 'This will cancel the answering machine for the entire phone service and remove the VOICEBOX number. Are you sure?',
        confirmLabel: 'Cancel AM',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.saving.set(true);
      this.api.cancelAnsweringMachine(this.phoneServiceId, String(this.auth.currentUserId))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.loadAll();
            this.toast.open('Answering machine cancelled', 'OK', { duration: 3000 });
          },
          error: (err) => {
            this.saving.set(false);
            this.toast.open(extractErrorMessage(err, 'Failed to cancel answering machine'), 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
    });
  }

  // --- Waiting Queue ---

  confirmActivateWq(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Activate Waiting Queue',
        message: 'Activate the waiting queue for this phone service?',
        confirmLabel: 'Activate',
        icon: 'play_arrow',
      },
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.saving.set(true);
      this.api.activateWaitingQueue(this.phoneServiceId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.loadAll();
            this.toast.open('Waiting queue activated', 'OK', { duration: 3000 });
          },
          error: (err) => {
            this.saving.set(false);
            this.toast.open(extractErrorMessage(err, 'Failed to activate waiting queue'), 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
    });
  }

  confirmDeactivateWq(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Deactivate Waiting Queue',
        message: 'Deactivate the waiting queue for this phone service? This will stop call queuing.',
        confirmLabel: 'Deactivate',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.saving.set(true);
      this.api.deactivateWaitingQueue(this.phoneServiceId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.loadAll();
            this.toast.open('Waiting queue deactivated', 'OK', { duration: 3000 });
          },
          error: (err) => {
            this.saving.set(false);
            this.toast.open(extractErrorMessage(err, 'Failed to deactivate waiting queue'), 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
    });
  }
}
